import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Star, Check, Trash2, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { StarRating } from '@/components/StarRating';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import axios from 'axios';
import { toast } from 'sonner';
import { authService } from '@/utils/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminReviews = () => {
  const [pendingReviews, setPendingReviews] = useState([]);
  const [allReviews, setAllReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReview, setSelectedReview] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const [pendingRes, allRes] = await Promise.all([
        axios.get(`${API}/admin/reviews/pending`, {
          headers: authService.getAuthHeader()
        }),
        axios.get(`${API}/admin/reviews/all`, {
          headers: authService.getAuthHeader()
        })
      ]);
      
      setPendingReviews(pendingRes.data);
      setAllReviews(allRes.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
      toast.error('Ошибка загрузки отзывов');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (reviewId) => {
    try {
      await axios.put(`${API}/admin/reviews/${reviewId}/approve`, {}, {
        headers: authService.getAuthHeader()
      });
      toast.success('Отзыв одобрен');
      fetchReviews();
    } catch (error) {
      console.error('Error approving review:', error);
      toast.error('Ошибка одобрения');
    }
  };

  const handleDelete = async (reviewId) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот отзыв?')) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/reviews/${reviewId}`, {
        headers: authService.getAuthHeader()
      });
      toast.success('Отзыв удалён');
      fetchReviews();
    } catch (error) {
      console.error('Error deleting review:', error);
      toast.error('Ошибка удаления');
    }
  };

  const handleView = (review) => {
    setSelectedReview(review);
    setViewDialogOpen(true);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('ru-RU');
  };

  if (loading) {
    return <div className="text-center py-12" data-testid="loading">Загрузка...</div>;
  }

  return (
    <div data-testid="admin-reviews-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="flex items-center gap-3 mb-8">
          <Star className="w-8 h-8 text-accent" fill="#D4AF37" />
          <h1 className="font-heading text-4xl tracking-tight">Управление отзывами</h1>
        </div>

        <Tabs defaultValue="pending" className="w-full">
          <TabsList>
            <TabsTrigger value="pending" data-testid="tab-pending">
              На модерации ({pendingReviews.length})
            </TabsTrigger>
            <TabsTrigger value="all" data-testid="tab-all">
              Все отзывы ({allReviews.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pending" className="mt-6">
            {pendingReviews.length === 0 ? (
              <div className="text-center py-12 bg-background rounded-sm border border-border">
                <p className="text-muted-foreground">Нет отзывов на модерации</p>
              </div>
            ) : (
              <div className="bg-background rounded-sm border border-border overflow-hidden">
                <table className="w-full" data-testid="pending-reviews-table">
                  <thead className="bg-muted">
                    <tr>
                      <th className="px-6 py-4 text-left font-body text-sm uppercase tracking-wide">Имя</th>
                      <th className="px-6 py-4 text-left font-body text-sm uppercase tracking-wide">Email</th>
                      <th className="px-6 py-4 text-left font-body text-sm uppercase tracking-wide">Рейтинг</th>
                      <th className="px-6 py-4 text-left font-body text-sm uppercase tracking-wide">Комментарий</th>
                      <th className="px-6 py-4 text-left font-body text-sm uppercase tracking-wide">Дата</th>
                      <th className="px-6 py-4 text-left font-body text-sm uppercase tracking-wide">Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingReviews.map((review, index) => (
                      <tr key={review.id} className="border-t border-border hover:bg-muted/50" data-testid={`pending-review-${index}`}>
                        <td className="px-6 py-4 font-medium">{review.name}</td>
                        <td className="px-6 py-4 text-sm">{review.email}</td>
                        <td className="px-6 py-4">
                          <StarRating rating={review.rating} size={16} />
                        </td>
                        <td className="px-6 py-4 max-w-md">
                          <p className="text-sm text-muted-foreground truncate">{review.comment}</p>
                        </td>
                        <td className="px-6 py-4 text-sm">{formatDate(review.created_at)}</td>
                        <td className="px-6 py-4">
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleView(review)}
                              data-testid={`view-button-${index}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleApprove(review.id)}
                              className="text-green-600 hover:text-green-700"
                              data-testid={`approve-button-${index}`}
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDelete(review.id)}
                              className="text-red-600 hover:text-red-700"
                              data-testid={`delete-button-${index}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="all" className="mt-6">
            <div className="bg-background rounded-sm border border-border overflow-hidden">
              <table className="w-full" data-testid="all-reviews-table">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-6 py-4 text-left font-body text-sm uppercase tracking-wide">Имя</th>
                    <th className="px-6 py-4 text-left font-body text-sm uppercase tracking-wide">Рейтинг</th>
                    <th className="px-6 py-4 text-left font-body text-sm uppercase tracking-wide">Комментарий</th>
                    <th className="px-6 py-4 text-left font-body text-sm uppercase tracking-wide">Статус</th>
                    <th className="px-6 py-4 text-left font-body text-sm uppercase tracking-wide">Дата</th>
                    <th className="px-6 py-4 text-left font-body text-sm uppercase tracking-wide">Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {allReviews.map((review, index) => (
                    <tr key={review.id} className="border-t border-border hover:bg-muted/50" data-testid={`all-review-${index}`}>
                      <td className="px-6 py-4 font-medium">{review.name}</td>
                      <td className="px-6 py-4">
                        <StarRating rating={review.rating} size={16} />
                      </td>
                      <td className="px-6 py-4 max-w-md">
                        <p className="text-sm text-muted-foreground truncate">{review.comment}</p>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs rounded ${review.approved ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                          {review.approved ? 'Опубликован' : 'На модерации'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm">{formatDate(review.created_at)}</td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleView(review)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(review.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </motion.div>

      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="view-review-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl tracking-tight">Детали отзыва</DialogTitle>
          </DialogHeader>

          {selectedReview && (
            <div className="space-y-4 py-4">
              <div>
                <p className="font-body text-sm text-muted-foreground uppercase tracking-wide mb-1">Имя</p>
                <p className="font-medium">{selectedReview.name}</p>
              </div>

              <div>
                <p className="font-body text-sm text-muted-foreground uppercase tracking-wide mb-1">Email</p>
                <p>{selectedReview.email}</p>
              </div>

              <div>
                <p className="font-body text-sm text-muted-foreground uppercase tracking-wide mb-1">Рейтинг</p>
                <StarRating rating={selectedReview.rating} size={24} />
              </div>

              <div>
                <p className="font-body text-sm text-muted-foreground uppercase tracking-wide mb-1">Комментарий</p>
                <p className="whitespace-pre-wrap">{selectedReview.comment}</p>
              </div>

              <div>
                <p className="font-body text-sm text-muted-foreground uppercase tracking-wide mb-1">Язык</p>
                <p>{selectedReview.lang === 'lv' ? 'Latviešu' : 'Русский'}</p>
              </div>

              <div>
                <p className="font-body text-sm text-muted-foreground uppercase tracking-wide mb-1">Дата</p>
                <p>{formatDate(selectedReview.created_at)}</p>
              </div>

              <div>
                <p className="font-body text-sm text-muted-foreground uppercase tracking-wide mb-1">Статус</p>
                <p>{selectedReview.approved ? 'Опубликован' : 'На модерации'}</p>
              </div>

              {!selectedReview.approved && (
                <div className="flex gap-2 pt-4">
                  <Button
                    onClick={() => {
                      handleApprove(selectedReview.id);
                      setViewDialogOpen(false);
                    }}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Одобрить
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      handleDelete(selectedReview.id);
                      setViewDialogOpen(false);
                    }}
                    className="flex-1 text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Удалить
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
